import { useCallback, useEffect, useState } from 'react';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FormNewUserData } from 'dashboard/forms/FormNewUser';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Role } from 'dashboard/models/role';
import { Team } from 'dashboard/models/team';
import { User, UserStatus } from 'dashboard/models/user';
import AddUserModal from 'dashboard/views/DashboardUsersView/AddUserModal';
import IssueRecoveryCodeModal from 'dashboard/views/DashboardUsersView/IssueRecoveryCodeModal';
import UsersList from 'dashboard/views/DashboardUsersView/UsersList';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';

interface DashboardUsersViewProps {
  currentUser: User;
  featureFlags: FeatureFlagsMap;
  filterStatuses: UserStatus[];
  filterTeamIds: string[];
  hasSamlEnabled: boolean;
  roles: Role[];
  pageSize: number;
  paginationMeta: PaginationParams;
  recoveryCode: string | undefined;
  searchTerm: string;
  teams: Team[];
  users: User[];
  changeUserRoles: (user: User, roleIds: string[]) => Promise<void>;
  clearRecoveryCode: () => void;
  handleCreateUser: (values: FormNewUserData) => Promise<void>;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handleReactivateUser: (user: User) => Promise<void>;
  handleDestroyUser: (user: User) => Promise<void>;
  reinviteUser: (user: User) => Promise<void>;
  rotateRecoveryCode: (userId: string) => Promise<void>;
  setFilterStatuses: (statuses: UserStatus[]) => void;
  setFilterTeamIds: (ids: string[]) => void;
  setSearchTerm: (term: string) => void;
  showMessage: (message: string, messageType: MessageType, persist?: boolean) => void;
}

const DashboardUsersView = ({
  handleCreateUser,
  handleReactivateUser,
  handleDestroyUser,
  changeUserRoles,
  reinviteUser,
  hasSamlEnabled,
  searchTerm,
  filterStatuses,
  filterTeamIds,
  users,
  roles,
  teams,
  currentUser,
  recoveryCode,
  rotateRecoveryCode,
  clearRecoveryCode,
  setSearchTerm,
  setFilterStatuses,
  setFilterTeamIds,
  pageSize,
  paginationMeta,
  handlePageLinkClick,
  handlePageSizeClick,
  showMessage,
}: DashboardUsersViewProps) => {
  const [userToDestroy, setUserToDestroy] = useState(undefined);
  const [userToReactivate, setUserToReactivate] = useState(undefined);
  const [showAddUserModal, { enable: openAddUserModal, disable: closeAddUserModal }] =
    useToggle(false);

  const removeUser = (user: User) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setUserToDestroy(user);
  };

  const reactivateUser = (user: User) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setUserToReactivate(user);
  };

  const handleSubmitAddUserAndClose = useCallback(
    async (values: FormNewUserData) => {
      await handleCreateUser(values);
      closeAddUserModal();
    },
    [handleCreateUser],
  );

  const hideConfirmDeleteModal = () => {
    setUserToDestroy(undefined);
  };

  const hideConfirmReactivateModal = () => {
    setUserToReactivate(undefined);
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const confirmRemoveUser = () => handleDestroyUser(userToDestroy);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const confirmReactivateUser = () => handleReactivateUser(userToReactivate);

  const hasUserViewPermission = usePermission(Permission.UserView);

  useEffect(() => {
    if (!hasUserViewPermission) {
      showMessage(
        'Your roles do not grant you the ability to view all users.',
        MessageType.Notice,
        true,
      );
    }
  }, [hasUserViewPermission, showMessage]);

  return (
    <Container>
      <Header title='Users' />
      <Section>
        <UsersList
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterStatuses={filterStatuses}
          filterTeamIds={filterTeamIds}
          setFilterStatuses={setFilterStatuses}
          setFilterTeamIds={setFilterTeamIds}
          changeUserRoles={changeUserRoles}
          currentUser={currentUser}
          hasSamlEnabled={hasSamlEnabled}
          openAddUserModal={openAddUserModal}
          reactivateUser={reactivateUser}
          reinviteUser={reinviteUser}
          removeUser={removeUser}
          roles={roles}
          teams={teams}
          rotateRecoveryCode={rotateRecoveryCode}
          users={users}
        />
        <Pagination
          pageSize={pageSize}
          paginationParams={paginationMeta}
          handlePageLinkClick={handlePageLinkClick}
          handlePageSizeClick={handlePageSizeClick}
        />
        <ModalWrapper isOpen={showAddUserModal} handleCloseModal={closeAddUserModal}>
          <AddUserModal
            handleClose={closeAddUserModal}
            handleSubmitAddUser={handleSubmitAddUserAndClose}
            roles={roles}
          />
        </ModalWrapper>
        <ModalWrapper isOpen={userToDestroy != null} handleCloseModal={hideConfirmDeleteModal}>
          <ConfirmationModal
            cancelAction={hideConfirmDeleteModal}
            confirmAction={confirmRemoveUser}
            header='Remove User?'
            confirmActionText='Remove'
          >
            Are you sure you want to remove
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <strong>{userToDestroy?.attributes['email-address']}</strong> from the team?
          </ConfirmationModal>
        </ModalWrapper>
        <ModalWrapper
          isOpen={userToReactivate != null}
          handleCloseModal={hideConfirmReactivateModal}
        >
          <ConfirmationModal
            cancelAction={hideConfirmReactivateModal}
            confirmAction={confirmReactivateUser}
            header='Reactivate User?'
            confirmActionText='Reactivate'
          >
            <Paragraph>This user will need to go through account setup again.</Paragraph>
            <Spacer height={1} />
            <Paragraph>
              Warning: all history for
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              <strong>{userToReactivate?.attributes['email-address']}</strong> will be restored. Are
              you sure you want to proceed?
            </Paragraph>
          </ConfirmationModal>
        </ModalWrapper>
        <ModalWrapper isOpen={recoveryCode != null} handleCloseModal={clearRecoveryCode}>
          <IssueRecoveryCodeModal recoveryCode={recoveryCode} onClose={clearRecoveryCode} />
        </ModalWrapper>
      </Section>
    </Container>
  );
};

export default DashboardUsersView;
